import { ChangeDetectionStrategy, Component, computed, effect, Signal, signal, untracked } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { HierarchyTreeSelectComponent, HierarchyType } from '../../../hierarchy';
import { ReportParameterBase } from './report-parameter-base';

export interface HierarchyTreeParameterOptions {
    hierarchyType: HierarchyType;
}

@Component({
    selector: 'app-hierarchy-tree-select-parameter',
    imports: [HierarchyTreeSelectComponent],
    template: `
        <app-hierarchy-tree-select
            [selectedHierarchyNodeKeys]="hierarchyNodeKeys()"
            [id]="parameterKey()"
            [subscriberKey]="subscriberKey() ?? null"
            [hierarchyType]="parameterOptions().hierarchyType"
            selectionMode="checkbox"
            [placeholder]="subscriberKey() !== null && subscriberKey() !== undefined ? parameterDefinition().placeholder : 'Select a subscriber first'"
            [invalid]="isInvalid()"
            [disabled]="isDisabled()"
            (blur)="onBlur()"
            (loadingChange)="onLoadingChange($event)"
            (selectedHierarchyNodeKeysChange)="onHierarchyNodeKeysChange($event)"
        />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: HierarchyTreeSelectParameter,
            multi: true
        }
    ]
})
export class HierarchyTreeSelectParameter extends ReportParameterBase<number[], HierarchyTreeParameterOptions> {
    hierarchyNodeKeys = signal<number[]>([]);
    subscriberKey: Signal<number | null> = this.reportWorkspace.subscriberKey;

    override isDisabled = computed(() => this.reportWorkspace.isReportRunning() || this.isFormControlDisabled() || !this.subscriberKey());

    constructor() {
        super();

        /* Clear Options when Subscriber Changes */
        effect(() => {
            this.subscriberKey();
            const parameterDefinition = untracked(this.parameterDefinition);
            const parameterKey = untracked(this.parameterKey);

            if (!this.isInitialized) {
                return;
            }

            const value = (parameterDefinition.emptyValue as number[]) ?? [];
            this.hierarchyNodeKeys.set(value);

            this.reportWorkspace.setParam(parameterKey, value);

            this.onChange(value);

            this.formControl?.markAsUntouched();
            this.formControl?.markAsPristine();
        });
    }

    onHierarchyNodeKeysChange = (hierarchyNodeKeys: number[]) => {
        this.hierarchyNodeKeys.set(hierarchyNodeKeys);

        this.reportWorkspace.setParam(this.parameterKey(), hierarchyNodeKeys);

        this.onChange(hierarchyNodeKeys);
        this.markTouchedIfNotResetting();
    };

    onLoadingChange = (isLoading: boolean) => {
        this.reportWorkspace.setParamLoading(this.parameterKey(), isLoading);
    };

    onBlur() {
        this.onTouched();
    }

    // CVA callbacks
    writeValue(obj: number[]): void {
        this.hierarchyNodeKeys.set(obj);
    }
}
